import * as React from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { TimePicker as MuiTimePicker } from '@mui/x-date-pickers/TimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Box, FormLabel, SxProps, Theme, styled } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { FONT_ENUM } from '../../types/global/fonts.types';

dayjs.extend(utc);
dayjs.extend(timezone);

// Fix for TypeScript error: Property 'supportedValuesOf' does not exist on type 'typeof Intl'.
interface IntlConstructor {
    supportedValuesOf?(key: string): string[];
}
declare var Intl: IntlConstructor;

export interface ICustomDatePicker {
    label?: string;
    value?: string;
    onChange?: (e: any, timezone?: string) => void;
    minDate?: any;
    maxDate?: any;
    defaultValue?: any;
    ageLimit?: number;
    sx?: SxProps<Theme>;
    withTime?: boolean;
    withTimezone?: boolean;
    disabled?: boolean;
    timezone?: string;
    enableLabel?: boolean;
}

const StyledBox = styled(Box)(({ theme }) => ({
    width: '100%',
    border: '1px solid grey',
    borderRadius: '10px',
    overflow: 'hidden',
    color: 'white',
    backgroundColor: 'rgba(19, 21, 23, 1)',
    '&:focus-within': {
        border: `1px solid rgba(251, 188, 85, 1)`,
    },
}));

const saveTimeInTimezone = (
    date: Dayjs, // selectedDate from DatePicker
    time: Dayjs, // selectedTime from TimePicker
    timezone: string, // e.g. 'Africa/Lagos' or 'Etc/GMT-4'
): string => {
    const datetimeStr = `${date.format('YYYY-MM-DD')} ${time.format('HH:mm:ss')}`;

    const zoned = dayjs.tz(datetimeStr, timezone);

    return zoned.format('YYYY-MM-DDTHH:mm:ssZ'); // This will include the correct offset like +04:00 or -04:00
};

export const CustomDatePicker: React.FC<ICustomDatePicker> = ({
    label,
    value,
    onChange,
    minDate,
    maxDate,
    defaultValue,
    sx,
    withTime,
    withTimezone = true,
    disabled,
    timezone,
    enableLabel,
}) => {
    const { themeStyle } = useThemeContext();

    const timezoneOptions = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];
    const [selectedTimezone, setSelectedTimezone] = React.useState(timezone ?? dayjs.tz.guess());

    const defaultTime = dayjs().tz(selectedTimezone);
    const defaultDate = dayjs().tz(selectedTimezone);

    const [selectedTime, setSelectedTime] = React.useState<Dayjs>(defaultTime);
    const [selectedDate, setSelectedDate] = React.useState<Dayjs | null>(
        value ? (dayjs(value).isValid() ? dayjs(value).tz(selectedTimezone) : defaultDate) : defaultDate,
    );

    React.useEffect(() => {
        if (value && dayjs(value).isValid()) {
            const localTime = dayjs(value).tz(selectedTimezone);
            setSelectedTime(localTime);
            setSelectedDate(localTime);
        }
    }, [value, selectedTimezone]);

    const handleDateChange = (newDate: Dayjs | null) => {
        if (newDate) {
            setSelectedDate(newDate);

            const final = saveTimeInTimezone(newDate, selectedTime, selectedTimezone);
            onChange?.(final, selectedTimezone);
        }
    };

    const handleTimeChange = (newTime: Dayjs | null) => {
        if (newTime && selectedDate) {
            setSelectedTime(newTime);

            const final = saveTimeInTimezone(selectedDate, newTime, selectedTimezone);
            onChange?.(final, selectedTimezone);
        }
    };

    return (
        <>
            {enableLabel && (
                <FormLabel
                    style={{
                        maxWidth: 400,
                        width: '100%',
                        textAlign: 'left',
                        fontSize: 14,
                        fontStyle: 'normal',
                        fontWeight: '400',
                        color: theme[themeStyle].form_label,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                    }}>
                    {label}
                </FormLabel>
            )}
            <StyledBox
                sx={{
                    '&:focus-within': { border: `1px solid ${theme[themeStyle].highlight}` },
                    backgroundColor: theme[themeStyle].tag_white_light,
                    ...sx,
                }}>
                {withTimezone && (
                    <Box sx={{ mb: 1, pr: 1 }}>
                        <select
                            value={selectedTimezone}
                            onChange={e => {
                                const newTz = e.target.value;
                                setSelectedTimezone(newTz);

                                if (selectedDate && selectedTime) {
                                    if (onChange) {
                                        onChange(saveTimeInTimezone(selectedDate, selectedTime, newTz), newTz); // will include new offset like +04:00
                                    }
                                }
                            }}
                            style={{
                                width: '100%',
                                height: '40px',
                                backgroundColor: theme[themeStyle].gray_text,
                                color: theme[themeStyle].lightWhite,
                                border: 'none',
                                borderRadius: '6px',
                                padding: '0 8px',
                                outline: 'none',
                            }}>
                            {timezoneOptions.map((tz: any) => (
                                <option key={tz} value={tz}>
                                    {tz}
                                </option>
                            ))}
                        </select>
                    </Box>
                )}
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        value={selectedDate}
                        minDate={minDate ? dayjs(minDate).tz(selectedTimezone) : undefined}
                        maxDate={maxDate ? dayjs(maxDate).tz(selectedTimezone) : undefined}
                        disabled={disabled}
                        timezone="system"
                        onChange={handleDateChange}
                        sx={{
                            width: '100%',
                        }}
                        slotProps={{
                            textField: {
                                variant: 'filled',
                                inputProps: {
                                    style: {
                                        color: theme[themeStyle].lightWhite,
                                        backgroundColor: theme[themeStyle].gray_text,
                                        paddingTop: 0,
                                        paddingBottom: 0,
                                        height: '50px',
                                    },
                                },
                                InputProps: {
                                    disableUnderline: true,
                                },
                            },
                            layout: {
                                sx: {
                                    backgroundColor: theme[themeStyle].gray_text,
                                    color: theme[themeStyle].lightWhite,
                                    '& .MuiDayCalendar-weekDayLabel': {
                                        color: `${theme[themeStyle].light_gray_text}`,
                                    },
                                },
                            },
                            nextIconButton: {
                                sx: {
                                    color: theme[themeStyle].primary_orange,
                                    ':disabled': {
                                        color: `${theme[themeStyle].light_gray_text} !important`,
                                    },
                                },
                            },
                            previousIconButton: {
                                sx: {
                                    color: theme[themeStyle].primary_orange,
                                    ':disabled': {
                                        color: `${theme[themeStyle].light_gray_text} !important`,
                                    },
                                },
                            },
                            day: {
                                sx: {
                                    color: theme[themeStyle].lightWhite,
                                    ':disabled': {
                                        color: `${theme[themeStyle].light_gray_text} !important`,
                                    },
                                    headerColor: 'white !important',
                                },
                            },
                            switchViewButton: {
                                sx: {
                                    color: theme[themeStyle].primary_orange,
                                },
                            },
                            openPickerButton: {
                                sx: {
                                    color: theme[themeStyle].lightWhite,
                                },
                            },
                        }}
                    />
                    {withTime && (
                        <MuiTimePicker
                            value={selectedTime}
                            disabled={disabled}
                            // minTime={minDate ? dayjs(minDate).tz(selectedTimezone) : undefined}
                            timeSteps={{
                                hours: 1,
                                minutes: 1,
                                seconds: 1,
                            }}
                            sx={{
                                width: '100%',
                            }}
                            timezone="system"
                            onChange={handleTimeChange}
                            slotProps={{
                                textField: {
                                    variant: 'filled',
                                    inputProps: {
                                        style: {
                                            color: theme[themeStyle].lightWhite,
                                            backgroundColor: theme[themeStyle].gray_text,
                                            paddingTop: 0,
                                            paddingBottom: 0,
                                            height: '50px',
                                        },
                                    },
                                    InputProps: {
                                        disableUnderline: true,
                                    },
                                },
                                layout: {
                                    sx: {
                                        backgroundColor: theme[themeStyle].gray_text,
                                        color: theme[themeStyle].lightWhite,
                                        '& .MuiDayCalendar-weekDayLabel': {
                                            color: `${theme[themeStyle].light_gray_text}`,
                                        },
                                    },
                                },
                                openPickerButton: {
                                    sx: {
                                        color: theme[themeStyle].lightWhite,
                                    },
                                },
                            }}
                        />
                    )}
                </LocalizationProvider>
            </StyledBox>
        </>
    );
};
